/**
 * deficiencyUtils.js
 *
 * Parses all *Def boolean flags from the nested API record and returns:
 *
 *   deficiencyMap    — { [docKey]: { flagName, filePath } }
 *                      for every field/doc marked as deficient
 *   isDeficiencyMode — true if ANY *Def flag is true in the record
 *
 * When isDeficiencyMode is true:
 *   - Docs in deficiencyMap  → editable + red highlight
 *   - Everything else        → read-only, uploads disabled
 *
 * The `flagName` (e.g. "isAadharCardDef") is sent as `deficiencyIn`
 * in the deficiency API payload.
 */

/**
 * @param {object} record  — raw API record from get-entities
 * @returns {{ deficiencyMap: object, isDeficiencyMode: boolean }}
 */
export const parseDeficiencies = (record) => {
  if (!record) return { deficiencyMap: {}, isDeficiencyMode: false };

  const prop = record.proprietorshipDetails || {};
  const partner = record.partnershipDetails || {};
  const llp = record.llpDetails || {};
  const pvt = record.privatePublicLimitedCompanyDetails || {};
  const trust = record.trustDetails || {};
  const msme = record.msmeDetails || {};
  const utility = record.utilityBillDetails || {};
  const bank = record.bankDetails || {};

  /**
   * Map: internal doc key → { flagName, isDef, filePath }
   *
   * flagName  = the exact API *Def field name — sent as `deficiencyIn`
   * isDef     = boolean value of that flag
   * filePath  = the uploaded file path for this doc (used in deficiency payload)
   */
  const ENTRIES = [
    // Proprietorship
    {
      key: "doc_aadhar",
      flagName: "isAadharCardDef",
      isDef: prop.isAadharCardDef,
      filePath: prop.aadharCardPath || "",
    },
    {
      key: "doc_pan",
      flagName: "isPANCardDef",
      isDef: prop.isPANCardDef,
      filePath: prop.panCardPath || "",
    },

    // Partnership
    {
      key: "doc_partnershipDeed",
      flagName: "isPartnershipDeedDef",
      isDef: partner.isPartnershipDeedDef || llp.isPartnershipDeedDef,
      filePath: partner.partnershipDeedPath || llp.partnershipDeedPath || "",
    },
    {
      key: "doc_aadharBoth",
      flagName: "isAadharOfBothPartnersDef",
      isDef: partner.isAadharOfBothPartnersDef || llp.isAadharOfBothPartnersDef,
      filePath:
        partner.aadharOfBothPartnersPath || llp.aadharOfBothPartnersPath || "",
    },

    // LLP
    {
      key: "doc_llpCertificate",
      flagName: "isLLPCertificateDef",
      isDef: llp.isLLPCertificateDef,
      filePath: llp.uploadLLPCertificatePath || "",
    },

    // Private / Public Limited
    {
      key: "doc_coi",
      flagName: "isCertificateOfIncorporationDef",
      isDef: pvt.isCertificateOfIncorporationDef,
      filePath: pvt.certificateOfIncorporationPath || "",
    },
    {
      key: "doc_directorPan",
      flagName: "isDirectorPanDef",
      isDef: pvt.isDirectorPanDef,
      filePath: pvt.directorPANPath || "",
    },
    {
      key: "doc_directorAadhar",
      flagName: "isDirectorAadharDef",
      isDef: pvt.isDirectorAadharDef,
      filePath: pvt.directorAadharPath || "",
    },

    // Trust
    {
      key: "doc_trustDeed",
      flagName: "isTrustDeedDef",
      isDef: trust.isTrustDeedDef,
      filePath: trust.trustDeedPath || "",
    },
    {
      key: "doc_panTrust",
      flagName: "isPANOfTrustDef",
      isDef: trust.isPANOfTrustDef,
      filePath: trust.trustPanPath || "",
    },
    {
      key: "doc_panTrustee",
      flagName: "isPANOfTrusteeDef",
      isDef: trust.isPANOfTrusteeDef,
      filePath: trust.trusteePanPath || "",
    },
    {
      key: "doc_aadharTrustee",
      flagName: "isAadharOfTrusteeDef",
      isDef: trust.isAadharOfTrusteeDef,
      filePath: trust.trusteeAadharPath || "",
    },

    // MSME
    {
      key: "doc_msme_certificate",
      flagName: "isMSMECertificateDef",
      isDef: msme.isMSMECertificateDef,
      filePath: msme.msmeCertificatePath || "",
    },

    // Utility bill
    {
      key: "doc_utility_bill",
      flagName: "isUtilityBillDocumentDef",
      isDef: utility.isUtilityBillDocumentDef,
      filePath: utility.utilityBillDocumentPath || "",
    },

    // Bank
    {
      key: "doc_bank_proof",
      flagName: "isPassbookOrCancelledChequeDef",
      isDef: bank.isPassbookOrCancelledChequeDef,
      filePath: bank.passbookOrCancelledChequePath || "",
    },
    {
      key: "third_party_file",
      flagName: "isSignedFormDef",
      isDef: bank.isSignedFormDef,
      filePath: bank.uploadSignedFormPath || "",
    },
  ];

  // Build deficiencyMap — only entries where isDef === true
  const deficiencyMap = {};
  for (const entry of ENTRIES) {
    if (entry.isDef === true) {
      deficiencyMap[entry.key] = {
        flagName: entry.flagName, // sent as `deficiencyIn` in API payload
        filePath: entry.filePath, // existing file path
      };
    }
  }

  const isDeficiencyMode = Object.keys(deficiencyMap).length > 0;

  return { deficiencyMap, isDeficiencyMode };
};

/**
 * Returns true if a given doc key is deficient.
 */
export const isDocDeficient = (docKey, deficiencyMap) =>
  !!deficiencyMap?.[docKey];

/**
 * In deficiency mode:
 *   - If the doc is deficient → allow upload (editable)
 *   - Otherwise              → block upload (readonly)
 */
export const canUploadDoc = (docKey, deficiencyMap, isDeficiencyMode) => {
  if (!isDeficiencyMode) return true;
  return isDocDeficient(docKey, deficiencyMap);
};

/**
 * Returns the `deficiencyIn` value for a doc key.
 * This is the API flag name (e.g. "isAadharCardDef") sent in the deficiency payload.
 */
export const getDeficiencyIn = (docKey, deficiencyMap) =>
  deficiencyMap?.[docKey]?.flagName || docKey;
